// Archiving a prop account asks how it ended (blown and why, passed, moved to live, closed after
// payouts, or just closing it) and the lesson, so My Trading Journey can tell its story. Skip archives it
// without the details.
import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { isProp } from './business';
import { useI18n } from './i18n';
import { BLOWN_CAUSES, END_CAUSE_NAMES } from './journey';
import { dayKey, formatMoney } from './pnl';
import { BEHAVIOR_CAUSES, BEHAVIOR_CAUSE_NAMES, endSnapshot } from './blownAccount';

// Whether archiving this account should ask how it ended.
export const asksHowItEnded = (account) => Boolean(account) && isProp(account) && !account.archived && !account.attemptOf;

// `defaults` ({ cause, behavior, lesson, end }) fills the answers in, e.g. from what the journal saw
// (BlownCard.js). Archiving with details also keeps a snapshot of the account (endSnapshot).
export function ArchiveAccountDialog({ account, trades = [], defaults = {}, onClose, onDone, onSessionExpired }) {
  const { t } = useI18n();
  const today = dayKey(new Date());
  const days = trades.map((x) => dayKey(x.date)).sort();
  const funded = account.type === 'Funded' || account.type === 'Live';
  const [outcome, setOutcome] = useState('failed');
  const [cause, setCause] = useState(defaults.cause || 'daily_loss');
  const [end, setEnd] = useState(defaults.end || (days.length ? days[days.length - 1] : today));
  const [lesson, setLesson] = useState(defaults.lesson || '');
  const snapshot = useMemo(() => endSnapshot(account, trades), [account, trades]);
  const [behavior, setBehavior] = useState(defaults.behavior || (snapshot && snapshot.cause) || '');
  // The mistakes the journal found in this account come first, with what they cost.
  const costOf = Object.fromEntries(((snapshot && snapshot.mistakes) || []).map((m) => [m.kind, m.cost]));
  const behaviorChoices = [...BEHAVIOR_CAUSES].sort((a, b) => (costOf[a] ?? 0) - (costOf[b] ?? 0));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const CHOICES = [
    ['failed', '💥', 'Blown'],
    ...(funded ? [] : [['passed', '🏁', 'Passed']]),
    ...(account.type === 'Funded' ? [['went_live', '🚀', 'Moved to live']] : []),
    ...(funded ? [['payout_done', '💵', 'Closed after payouts']] : []),
    ['closed', '📦', 'Just closing it'],
  ];

  async function archive(details) {
    setSaving(true);
    setError('');
    try {
      const body = details
        ? {
          archived: true, attemptEndedAt: end,
          outcome: ['failed', 'passed'].includes(outcome) ? outcome : '',
          endCause: outcome === 'failed' ? cause : outcome === 'passed' ? '' : outcome,
          lesson: lesson.trim(),
          ...(outcome === 'failed' ? { behaviorCause: behavior || undefined } : {}),
          ...(snapshot ? { endSnapshot: snapshot } : {}),
        }
        : { archived: true };
      const updated = await api(`/accounts/${account._id}`, { method: 'PATCH', body });
      onDone(updated);
    } catch (err) {
      if (err.status === 401 && onSessionExpired) onSessionExpired();
      else setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal ended-modal" role="dialog" aria-modal="true" aria-labelledby="ended-title" onSubmit={(e) => { e.preventDefault(); archive(true); }}>
        <div className="modal-header">
          <div>
            <h2 id="ended-title">{t('How did it end?')}</h2>
            <p className="manual-trade-day">{account.name}</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>

        <p className="field-hint">{t('Archiving keeps its trades and history. Two quick answers and My Trading Journey can tell its story.')}</p>

        <div className="ended-choices" role="radiogroup" aria-label={t('How did it end?')}>
          {CHOICES.map(([id, icon, label]) => (
            <button key={id} type="button" role="radio" aria-checked={outcome === id} className={`ended-choice${outcome === id ? ' on' : ''}`} onClick={() => setOutcome(id)}>
              <span aria-hidden="true">{icon}</span> {t(label)}
            </button>
          ))}
        </div>

        {outcome === 'failed' && (
          <div className="ended-causes">
            <span className="ended-label">{t('What ended it?')}</span>
            <div>
              {BLOWN_CAUSES.map((c) => (
                <button key={c} type="button" aria-pressed={cause === c} className={`ended-cause${cause === c ? ' on' : ''}`} onClick={() => setCause(c)}>{t(END_CAUSE_NAMES[c])}</button>
              ))}
            </div>
          </div>
        )}

        {outcome === 'failed' && (
          <div className="ended-causes">
            <span className="ended-label">{t('What caused it?')}</span>
            <div>
              {behaviorChoices.map((c) => (
                <button key={c} type="button" aria-pressed={behavior === c} className={`ended-cause${behavior === c ? ' on' : ''}`} onClick={() => setBehavior(behavior === c ? '' : c)}>
                  {t(BEHAVIOR_CAUSE_NAMES[c])}{costOf[c] !== undefined && costOf[c] < 0 ? ` · ${formatMoney(costOf[c])}` : ''}
                </button>
              ))}
            </div>
          </div>
        )}

        {outcome === 'went_live' && <p className="field-hint ended-live">{t('The top of the ladder. Add your live account on the Accounts page so its trades and payouts count in your journey.')}</p>}

        <div className="fields ended-fields">
          <label>
            {t('The lesson (optional)')}
            <input value={lesson} onChange={(e) => setLesson(e.target.value)} maxLength={300} placeholder={t('e.g. After 2 losses, the day is over.')} />
          </label>
          <label>
            {t('Ended on')}
            <input type="date" value={end} max={today} onChange={(e) => setEnd(e.target.value || today)} required />
          </label>
        </div>

        {error && <p className="error">{error}</p>}

        <div className="modal-actions">
          <button type="submit" disabled={saving}>{saving ? t('Saving…') : t('Archive')}</button>
          <button type="button" className="link-button" disabled={saving} onClick={() => archive(false)}>{t('Skip, just archive it')}</button>
        </div>
      </form>
    </div>
  );
}
